import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { Chip, Collapse, Icon, TextField } from '@mui/material';
import { AutoComplete } from '../Autocomplete';
import { get, size, uniqBy, sortBy, first, noop, orderBy } from '../../lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useStyles } from '../form/styles';
import { ChipContainer, StyledFormControl } from './styles';
import { SimpleSort } from '../sortable-list/simple';
import { Loader } from '../../styles';
import { customSearch, ensureArray } from '../../utils';

export const getTradeName = (trade) => {
  if (!trade) {
    return '';
  }
  return trade.name || trade.defaultName;
};

export const useTradeTypes = () => {
  const trades = useRetrieveData(
    { url: '/trade-types' },
    {
      version: 'v2',
      build: ['tradeTypes'],
      useCache: {
        reload: 10,
      },
      params: {
        filter: { enabled: 1 },
      },
      transformData: (d) => {
        d = d.map((dd) => ({ ...dd, _name: getTradeName(dd).toLowerCase() }));
        return sortBy(d, '_name');
      },
    }
  );

  return trades;
};

export const TradeSelectComp = forwardRef((props, ref) => {
  const { followValue, borderless, defaultsGroupLabel, ...passthroughProps } = props;
  const classes = useStyles({ showPosition: false });

  const [selected, setSelected] = useState(props.value || []);
  // Edit forms already have trades. Don't echo that value on mount — checklist
  // defaults are only a visual reference there, not a new selection.
  const suppressOnChange = useRef(!!followValue || !!props.skipDefaults);
  const [data, setData] = useState([]);
  const [checklist, setchecklist] = useState();
  const setByDefault = useRef(false);

  const sortable = get(props, 'sortable', false);
  const hasMutedTradeIds = Array.isArray(props.mutedTradeIds);
  const mutedTradeIds = new Set((props.mutedTradeIds || []).map((id) => String(id)));

  const formatData = (d = data) => {
    let all = [];
    const checklistTrades = get(checklist, 'tradeTypes', []).filter((t) => get(t, 'enabled', true));
    const hasChecklistTrades = !!size(checklistTrades);
    if (hasChecklistTrades) {
      const groupLabel = defaultsGroupLabel || get(checklist, 'name');
      checklistTrades.map((t) => {
        all.push({ ...t, groupBy: groupLabel });
      });
    }

    all = [...all, ...d.map((dd) => ({ ...dd, groupBy: hasChecklistTrades ? 'All Types' : undefined }))];

    setData(uniqBy(orderBy(all, 'order'), 'id'));
  };

  const trades = useTradeTypes();

  const clid = get(props, 'checklist.checklistId', get(props, 'checklist.id', null));
  const clist = useRetrieveData(
    { url: `/checklists/${clid}` },
    {
      build: ['checklists'],
      useCache: {
        // Edit (skipDefaults) must not refetch the checklist. The cached default
        // trade is only compared for chip styling.
        reload: props.skipDefaults ? () => false : 10,
      },
      params: {
        include: 'tradeTypes',
      },
      waitFor: clid,
      transformData: (d) => {
        const ch = first(d);
        setchecklist(ch);
        const enabledTrades = get(ch, 'tradeTypes', []).filter((t) => get(t, 'enabled', true));
        const vals = ensureArray(first(orderBy(enabledTrades, 'order')));
        setSelected((s) => {
          const shouldSet = !size(s) && !props.skipDefaults;
          if (shouldSet) {
            setByDefault.current = true;
          } else {
            setByDefault.current = false;
          }
          return shouldSet ? vals : s;
        });
        props.onChecklistTradesLoaded && props.onChecklistTradesLoaded(vals);
      },
    }
  );

  const clistloading = clid && clist.loading;

  useEffect(() => {
    formatData();
  }, [clid, checklist]);

  useEffect(() => {
    if (clid && setByDefault.current) {
      setSelected([]);
    }
  }, [clid]);

  useEffect(() => {
    formatData(trades.data);
  }, [trades.data]);

  const tradeIdsKey = (trades = []) =>
    (trades || [])
      .map((trade) => get(trade, 'id'))
      .filter((id) => id !== undefined && id !== null && id !== '')
      .map((id) => String(id))
      .join(',');

  useEffect(() => {
    // followValue is controlled by the parent. Echoing selected back replaces
    // header-applied trades with this field's previous selection.
    if (followValue) return;
    if (suppressOnChange.current) {
      suppressOnChange.current = false;
      return;
    }
    props.onChange && props.onChange(selected);
  }, [selected]);

  useEffect(() => {
    if (!followValue) return;
    const next = props.value || [];
    setSelected((current) => {
      if (tradeIdsKey(current) === tradeIdsKey(next)) return current;
      suppressOnChange.current = true;
      return next;
    });
  }, [followValue, props.value]);

  const deleteSelected = (s, i) => {
    const sel = [...selected];
    sel.splice(i, 1);
    setSelected(sel);
    setByDefault.current = false;
  };

  const onSort = (result) => {
    setSelected(result.map((r) => ({ ...r, content: undefined })));
  };

  const renderChip = (s, i) => {
    const chipStyle = hasMutedTradeIds
      ? { opacity: mutedTradeIds.has(String(get(s, 'id'))) ? 0.6 : 1.0 }
      : undefined;

    return <Chip size="small" style={chipStyle} icon={sortable ? <Icon>drag_indicator</Icon> : undefined} label={getTradeName(s)} onDelete={props.disabled ? undefined : () => deleteSelected(s, i)} />;
  };

  // PFM-2609: in normal edit mode the selected trades render inside the
  // Autocomplete input itself (via renderValue). In view (disabled) mode and
  // in sortable mode (admin checklist editor) we still need the external
  // ChipContainer — view mode hides the input entirely, and sortable mode
  // needs the SimpleSort drag-and-drop list which the input can't provide.
  const useExternalChipContainer = props.disabled || sortable;

  const renderValueInsideInput = (value, getItemProps) =>
    value.map((option, index) => {
      const { key, onDelete, ...itemProps } = getItemProps({ index });
      const chipStyle = hasMutedTradeIds
        ? { opacity: mutedTradeIds.has(String(get(option, 'id'))) ? 0.6 : 1.0 }
        : undefined;
      return (
        <Chip
          key={key}
          size="small"
          style={chipStyle}
          label={getTradeName(option)}
          {...itemProps}
          onDelete={onDelete}
          sx={(theme) => ({
            cursor: 'pointer',
            '&:hover .MuiChip-deleteIcon': {
              color: theme.alpha(theme.palette.text.primary, 0.4),
            },
          })}
          onMouseDown={(event) => {
            // Keep the field from focusing, which would open the list.
            event.preventDefault();
            event.stopPropagation();
          }}
          onMouseUp={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (event.target.closest('.MuiChip-deleteIcon')) {
              return;
            }
            onDelete?.(event);
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        />
      );
    });

  return (
    <StyledFormControl variant="standard">
      {useExternalChipContainer && (
        <Collapse in={!!size(selected)}>
          <ChipContainer>
            {sortable && <SimpleSort onSort={onSort} items={selected.map((s, i) => ({ ...s, content: renderChip(s, i) }))} />}
            {!sortable && selected.map(renderChip)}
          </ChipContainer>
        </Collapse>
      )}

      <div className={!props.disabled && !borderless ? classes.formWrap : undefined} style={props.disabled ? { display: 'none' } : undefined}>
        {clistloading && <Loader />}
        {!clistloading && (
          <AutoComplete
            filterOptions={(o, s) => customSearch(o, s, { key: getTradeName })}
            classes={{ groupLabel: classes.primaryColor }}
            topOffset={-180}
            variant="standard"
            multiple={get(props, 'multiple', true)}
            options={data}
            groupBy={(opt) => opt.groupBy}
            getOptionLabel={getTradeName}
            getOptionKey={(d) => d.id}
            disableCloseOnSelect
            disableClearable
            openOnFocus
            renderInput={(params) => {
              const placeholder = followValue && size(selected) ? '' : passthroughProps.placeholder;
              return (
                <TextField
                  variant="standard"
                  {...passthroughProps}
                  onChange={noop}
                  label={undefined}
                  {...params}
                  placeholder={placeholder}
                  slotProps={{
                    ...params.slotProps,
                    htmlInput: {
                      ...params.slotProps?.htmlInput,
                      placeholder,
                    },
                  }}
                />
              );
            }}
            getOptionSelected={(option, value) => option.id === value.id}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            {...passthroughProps}
            // renderValue placed AFTER {...props} so consumer spreads can't
            // re-introduce the duplicate-chip rendering we're suppressing.
            renderValue={useExternalChipContainer ? () => null : renderValueInsideInput}
            value={selected || []}
            label={undefined}
            onChange={(e, vals) => {
              const upd = uniqBy(vals, 'id');
              setSelected(upd);
              setByDefault.current = false;
              props.setIsTradeSelectDirty?.(true);
              if (followValue) {
                props.onChange && props.onChange(upd);
              }
            }}
          />
        )}
      </div>
    </StyledFormControl>
  );
});

export const TradeSelect = (props) => {
  const [selected, setSelected] = useState(props.value || []);

  useEffect(() => {
    setSelected(props.value || []);
  }, [props.value]);

  const onChange = (sel) => {
    setSelected(sel);
    props.onChange && props.onChange(sel);
  };

  const onChecklistTradesLoaded = (c) => {
    if (!size(selected) && !props.skipDefaults) {
      onChange(c);
    }
    props.onChecklistTradesLoaded && props.onChecklistTradesLoaded(c);
  };

  const type = get(props, 'type', 'default');

  if (type === 'inline') {
    const { type: _type, modalTitle: _modalTitle, ...inlineProps } = props;

    return (
      <TradeSelectComp
        {...inlineProps}
        followValue
        borderless
        defaultsGroupLabel="Defaults"
        value={selected}
        onChange={onChange}
        onChecklistTradesLoaded={onChecklistTradesLoaded}
        placeholder={props.placeholder || 'None'}
      />
    );
  }

  return <TradeSelectComp {...props} placeholder={'Select Trade Types...'} />;
};
